import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserSession, AdminLevel } from '@/types/session';

const SESSION_QUERY_KEY = ['session'];

/**
 * V4 session blob shape returned by GET /v4/auth/session. Identity + RBAC
 * only. No Mongo-shaped fields (organisation_*, usergroups, sensorgroups,
 * tags, configtags, admin) — all authorization in the new UI is PG roles +
 * teams + permission_strings, all sourced from PostgreSQL.
 */
interface V4SessionBlob {
  pg_id: string;
  mongo_user_id: string | null;
  email: string | null;
  username: string | null;
  firstname: string | null;
  lastname: string | null;
  language?: 'en' | 'fr' | null;
  profile_photo_url?: string | null;
  postgres_roles: string[];
  teams: Array<{ id: string; name: string; role?: string | null }>;
  employment_type: string | null;
  employment_end_date: string | null;
  permission_strings: string[];
  enabled: boolean;
  ai_investigation_enabled?: boolean;
  impersonatedBy?: UserSession['impersonatedBy'];
  impersonationStartedAt?: number | null;
}

function mapV4ToUserSession(v4: V4SessionBlob): UserSession {
  // V4 session: identity + RBAC. Legacy fields (organisation/usergroups/admin)
  // are stubbed because they have no v4 source of truth and must not be read.
  return {
    _id: v4.pg_id,
    pg_id: v4.pg_id,
    email: v4.email ?? '',
    username: v4.username ?? '',
    firstname: v4.firstname ?? '',
    lastname: v4.lastname ?? '',
    language: (v4.language ?? 'en') as 'en' | 'fr',
    profile_photo_url: v4.profile_photo_url ?? null,
    organisation: '',
    organisations: [],
    admin: '' as unknown as AdminLevel,
    usergroups: [],
    sensorgroups: [],
    tags: [],
    configtags: [],
    permission_strings: v4.permission_strings ?? [],
    postgres_roles: v4.postgres_roles ?? [],
    teams: v4.teams ?? [],
    employment_type: (v4.employment_type ?? null) as UserSession['employment_type'],
    employment_end_date: v4.employment_end_date ?? null,
    status: true,
    ai_investigation_enabled: v4.ai_investigation_enabled === true,
    impersonatedBy: v4.impersonatedBy ?? null,
    impersonationStartedAt: v4.impersonationStartedAt ?? null,
  };
}

/**
 * Fetch the current V4 session from the backend.
 *
 * V4 sessions are PostgreSQL-only — no MongoDB blob is consulted on the
 * server side. The returned `UserSession` is a compat-mapped view of the
 * V4 PG blob so that legacy components can keep reading their familiar
 * fields while the source of truth is fully on PG.
 */
async function fetchSession(): Promise<UserSession> {
  const response = await fetch('/api/v4/auth/session', {
    credentials: 'include',
  });

  if (response.status === 401) {
    throw new Error('Not authenticated');
  }

  if (!response.ok) {
    throw new Error(`Session fetch failed: ${response.status}`);
  }

  const data = await response.json();

  if (!data?.status || !data?.user) {
    throw new Error('Not authenticated');
  }

  return mapV4ToUserSession(data.user as V4SessionBlob);
}

/**
 * Hook to access and manage the current user session
 *
 * Uses React Query for caching and automatic refetching.
 * Session is cached for 10 minutes and refetched on window focus.
 */
export function useSession() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    retry: false, // Don't retry on auth failure
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
  });

  /**
   * Invalidate the session cache to force a refetch
   */
  const invalidateSession = () => {
    queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  };

  /**
   * Clear the session cache (useful after logout)
   */
  const clearSession = () => {
    queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
  };

  return {
    /** Current user session data */
    user: query.data ?? null,
    /** Whether the session is being fetched */
    isLoading: query.isLoading,
    /** Whether the user is authenticated */
    isAuthenticated: !!query.data?.status,
    /** Any error that occurred during session fetch */
    error: query.error,
    /** Refetch the session */
    refetch: query.refetch,
    /** Invalidate and refetch session */
    invalidateSession,
    /** Clear session from cache */
    clearSession,
  };
}

/**
 * Check if the current user has a specific admin level or higher
 * @deprecated Use useHasRole() or useHasAnyRole() instead - authorization is now PostgreSQL-based
 */
export function useHasAdminLevel(requiredLevels: AdminLevel[]): boolean {
  const { user } = useSession();
  if (!user) return false;
  return requiredLevels.includes(user.admin);
}

/**
 * Check if the current user has a specific permission
 */
export function useHasPermission(permission: string): boolean {
  const { user } = useSession();
  if (!user?.permission_strings) return false;
  return user.permission_strings.includes(permission);
}

/**
 * Check if the current user has all of the specified permissions
 */
export function useHasPermissions(permissions: string[]): boolean {
  const { user } = useSession();
  if (!user?.permission_strings) return false;
  return permissions.every((p) => user.permission_strings.includes(p));
}

/**
 * Check if the current user has a specific PostgreSQL role
 */
export function useHasRole(role: string): boolean {
  const { user } = useSession();
  if (!user) return false;
  return user.postgres_roles.includes(role);
}

/**
 * Check if the current user has any of the specified PostgreSQL roles
 */
export function useHasAnyRole(roles: string[]): boolean {
  const { user } = useSession();
  if (!user || !roles.length) return false;
  return roles.some((role) => user.postgres_roles.includes(role));
}

const LEADERSHIP_TEAM_NAME = 'Leadership';

/**
 * Current user's EOS team memberships. Empty array if not loaded.
 */
export function useUserTeams() {
  const { user } = useSession();
  return user?.teams ?? [];
}

/**
 * IDs of the current user's teams.
 */
export function useUserTeamIds(): string[] {
  return useUserTeams().map((t) => t.id);
}

/**
 * True if the current user is on the Leadership super-team.
 * Leadership members bypass per-team scoping for L10 entities (headlines,
 * KPIs, rocks, todos): they see and can manage every team's items.
 */
export function useIsLeadership(): boolean {
  return useUserTeams().some((t) => t.name === LEADERSHIP_TEAM_NAME);
}

/**
 * True if the user can manage L10 items belonging to ANY of `teamIds`.
 * - Leadership members can manage anything.
 * - Otherwise the user must be a member of every team listed.
 *
 * Pass an empty array to check generic create capability (any team membership
 * is enough since the user will pick teams in the form).
 */
export function useCanManageInTeams(teamIds: string[] = []): boolean {
  const isLeader = useIsLeadership();
  if (isLeader) return true;
  const userTeamIds = useUserTeamIds();
  if (userTeamIds.length === 0) return false;
  if (teamIds.length === 0) return true;
  return teamIds.every((tid) => userTeamIds.includes(tid));
}

/**
 * Per-row gate: can the user manage an existing item belonging to one or more
 * teams? Membership in ANY of the item's teams is enough (matches the server
 * `requireHeadlineTeamMutate` rule).
 */
export function useCanManageItemTeams(itemTeamIds: string[]): boolean {
  const isLeader = useIsLeadership();
  if (isLeader) return true;
  const userTeamIds = useUserTeamIds();
  if (userTeamIds.length === 0 || itemTeamIds.length === 0) return false;
  return itemTeamIds.some((tid) => userTeamIds.includes(tid));
}

/**
 * True when the current session is a "View as user" impersonation started by
 * a super-admin. The active `user` is the target; the impersonator is in
 * `user.impersonatedBy`.
 */
export function useIsImpersonating(): boolean {
  const { user } = useSession();
  return !!user?.impersonatedBy;
}

/**
 * Returns the original super-admin behind the active impersonation, or null.
 */
export function useImpersonator() {
  const { user } = useSession();
  return user?.impersonatedBy ?? null;
}

/**
 * True when the current user is an ATEK internal staff member (employee or
 * contractor), as defined by their PostgreSQL `employment_type`. Replaces
 * every legacy `user.organisation === ATEK_ORG_ID` check.
 *
 * Authorization in v4 is RBAC + employment_type — never Mongo organisations.
 */
export function useIsInternalStaff(): boolean {
  const { user } = useSession();
  if (!user) return false;
  if (user.employment_type === 'employee' || user.employment_type === 'contractor') return true;
  // Fallback: any user with a PG role is internal staff. PG roles are only
  // granted to ATEK employees/contractors.
  return (user.postgres_roles?.length ?? 0) > 0;
}
